import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

// Função para importar todas as imagens da galeria
const importAll = (r) => {
  return r.keys().map(r);
};

// Importa todas as imagens da pasta galeria
let images = [];
try {
  images = importAll(require.context('../assets/images/galeria', false, /\.(png|jpe?g|svg)$/));
} catch (e) {
  console.error("Erro ao carregar imagens da galeria:", e);
}

export const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [initialLimit, setInitialLimit] = useState(6);

  // Ajusta o limite inicial baseado na largura da tela
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) { // Desktop (lg)
        setInitialLimit(8);
      } else {
        setInitialLimit(6); // Mobile e Tablet
      }
    };

    handleResize(); // Executa ao montar o componente
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (images.length === 0) return null;

  // Mostra apenas o limite inicial, ou todas se clicar em "Ver Mais"
  const visibleImages = showAll ? images : images.slice(0, initialLimit);

  const openImage = (index) => {
    setSelectedImage(images[index]);
    setCurrentIndex(index);
    document.body.style.overflow = 'hidden'; // Impede o scroll do fundo
  };

  const closeImage = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto'; // Restaura o scroll
  };

  const nextImage = (e) => {
    e.stopPropagation();
    const nextIdx = (currentIndex + 1) % images.length;
    setSelectedImage(images[nextIdx]);
    setCurrentIndex(nextIdx);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    const prevIdx = (currentIndex - 1 + images.length) % images.length;
    setSelectedImage(images[prevIdx]);
    setCurrentIndex(prevIdx);
  };

  return (
    <section id="galeria" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Nossa Galeria de Trabalhos
          </h2>
          <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full mb-6"></div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Confira alguns dos nossos projetos finalizados e a qualidade do nosso acabamento em forros de PVC, drywall e muito mais.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {visibleImages.map((image, index) => (
            <div 
              key={index} 
              onClick={() => openImage(index)}
              className="relative aspect-square overflow-hidden rounded-xl shadow-md hover:shadow-xl transition-all duration-300 group cursor-pointer"
            >
              <img 
                src={image} 
                alt={`Trabalho Rei do Teto ${index + 1}`}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <span className="text-white text-xs md:text-sm font-medium px-3 py-1.5 md:px-4 md:py-2 border border-white rounded-full scale-90 group-hover:scale-100 transition-transform duration-300">
                  Ver Foto
                </span>
              </div>
            </div>
          ))}
        </div>

        {!showAll && images.length > initialLimit && (
          <div className="mt-12 text-center">
            <button 
              onClick={() => setShowAll(true)}
              className="inline-flex items-center px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-full transition-all shadow-lg hover:shadow-xl active:scale-95"
            >
              Ver Todas as Fotos ({images.length})
            </button>
          </div>
        )}
      </div>

      {/* Modal / Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4 md:p-10"
          onClick={closeImage}
        >
          <button 
            onClick={closeImage}
            className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors"
          >
            <X size={40} />
          </button>

          <button 
            onClick={prevImage}
            className="absolute left-4 md:left-10 text-white/70 hover:text-white transition-colors p-2"
          >
            <ChevronLeft size={48} />
          </button>

          <img 
            src={selectedImage} 
            alt="Trabalho ampliado"
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl animate-in zoom-in duration-300"
          />

          <button 
            onClick={nextImage}
            className="absolute right-4 md:right-10 text-white/70 hover:text-white transition-colors p-2"
          >
            <ChevronRight size={48} />
          </button>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 font-medium bg-white/10 px-4 py-1 rounded-full backdrop-blur-sm">
            {currentIndex + 1} / {images.length}
          </div>
        </div>
      )}
    </section>
  );
};
